import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { getUserDetails, updateUserProfile } from '../api/user';

const PageTitle = styled.h1`
  font-size: 32px;
  margin-bottom: 30px;
`;

const InfoContainer = styled.div`
  max-width: 600px;
  background-color: white;
  padding: 30px;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
`;

const InfoGroup = styled.div`
  margin-bottom: 25px;
`;

const EditableInfoGroup = styled.div`
  margin-bottom: 25px;
  display: flex;
  align-items: flex-end;
  gap: 15px;
`;

const Label = styled.div`
  font-weight: 600;
  margin-bottom: 8px;
  color: #555;
  font-size: 14px;
`;

const Value = styled.div`
  font-size: 16px;
  padding: 12px;
  background-color: #f5f5f5;
  border-radius: 4px;
  border: 1px solid #eee;
`;

const Input = styled.input`
  font-size: 16px;
  padding: 12px;
  border-radius: 4px;
  border: 1px solid #ddd;
  flex: 1;
  min-width: 200px;
  
  &:focus {
    outline: none;
    border-color: #2196f3;
    box-shadow: 0 0 0 2px rgba(33, 150, 243, 0.2);
  }
`;

const SaveButton = styled.button`
  background-color: #2196f3;
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.2s ease;
  
  &:hover {
    background-color: #1976d2;
  }
  
  &:disabled {
    background-color: #ccc;
    cursor: not-allowed;
  }
  
  &:active {
    background-color: #1565c0;
  }
`;

const StatusMessage = styled.div<{ isError?: boolean }>`
  margin-top: 20px;
  padding: 10px;
  border-radius: 4px;
  text-align: center;
  background-color: ${props => props.isError ? '#ffebee' : '#e8f5e9'};
  color: ${props => props.isError ? '#c62828' : '#2e7d32'};
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 300px;
  font-size: 18px;
  color: #666;
`;

const SettingsPage: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  
  // User fields
  const [username, setUsername] = useState('');
  const [bzs_username, setBzsUsername] = useState('');
  const [editableBzsUsername, setEditableBzsUsername] = useState('');
  const [phone_number, setPhoneNumber] = useState('');
  const [country, setCountry] = useState('');

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        setLoading(true);
        setError('');
        
        // Get logged in user from localStorage
        const storedUser = localStorage.getItem('user');
        if (!storedUser) {
          setError('Nie ste prihlásený');
          setLoading(false);
          return;
        }
        
        const { username } = JSON.parse(storedUser);
        
        // Fetch user details from server
        const response = await getUserDetails(username);
        
        if (!response.success) {
          setError(response.message || 'Nepodarilo sa načítať údaje');
          setLoading(false);
          return;
        }
        
        // Set user data
        setUsername(response.user.username || '');
        setBzsUsername(response.user.bzs_username || '');
        setEditableBzsUsername(response.user.bzs_username || '');
        setPhoneNumber(response.user.phone_number || '');
        setCountry(response.user.country || '');
        
        setLoading(false);
      } catch (err) {
        console.error('Error fetching user data:', err);
        setError('Chyba pri načítaní údajov');
        setLoading(false);
      }
    };
    
    fetchUserData();
  }, []);

  const handleSaveBzsUsername = async () => {
    if (!editableBzsUsername.trim()) {
      setError('BZS používateľské meno nemôže byť prázdne');
      return;
    }

    try {
      setSaving(true);
      setError('');
      setSuccessMessage('');

      const response = await updateUserProfile(username, editableBzsUsername.trim());
      
      if (!response.success) {
        setError(response.message || 'Nepodarilo sa uložiť zmeny');
        return;
      }

      // Update the current bzs_username state
      setBzsUsername(editableBzsUsername.trim());
      setSuccessMessage('BZS používateľské meno bolo úspešne aktualizované');
      
      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccessMessage('');
      }, 3000);

    } catch (err) {
      console.error('Error saving bzs username:', err);
      setError('Chyba pri ukladaní zmien');
    } finally {
      setSaving(false);
    }
  };

  const hasChanges = editableBzsUsername.trim() !== bzs_username;

  if (loading) {
    return (
      <div>
        <PageTitle>Nastavenia používateľa</PageTitle>
        <LoadingContainer>Načítavam údaje používateľa...</LoadingContainer>
      </div>
    );
  }

  return (
    <div>
      <PageTitle>Nastavenia používateľa</PageTitle>
      
      <InfoContainer>
        {error && <StatusMessage isError>{error}</StatusMessage>}
        {successMessage && <StatusMessage>{successMessage}</StatusMessage>}
        
        <InfoGroup>
          <Label>Používateľské meno</Label>
          <Value>{username}</Value>
        </InfoGroup>
        
        <EditableInfoGroup>
          <div style={{ flex: 1 }}>
          <Label>Používateľské meno BZS</Label>
            <Input
              type="text"
              value={editableBzsUsername}
              onChange={(e) => setEditableBzsUsername(e.target.value)}
              placeholder="Zadajte BZS používateľské meno"
              disabled={saving}
            />
          </div>
          <SaveButton
            onClick={handleSaveBzsUsername}
            disabled={saving || !hasChanges}
          >
            {saving ? 'Ukladám...' : 'Uložiť'}
          </SaveButton>
        </EditableInfoGroup>
        
        <InfoGroup>
          <Label>Telefónne číslo</Label>
          <Value>{phone_number || 'Nie je zadané'}</Value>
        </InfoGroup>
        
        <InfoGroup>
          <Label>Krajina</Label>
          <Value>{country}</Value>
        </InfoGroup>
      </InfoContainer>
    </div>
  );
};

export default SettingsPage; 